import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { Dayjs } from 'dayjs';
import { DaterangepickerDirective, LocaleConfig } from 'ngx-daterangepicker-material';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { LocaleConfigDate, rangesDate } from 'src/app/constants/date-config.const';
import { Router } from '@angular/router';
import { BaseService } from 'src/app/services/base.service';
import { INVOICE_HISTORY } from 'src/app/constants/endpoint.const';
import { detailInvoiceType } from 'src/app/models/invoices.model';

interface StatusItem {
  id: string;
  label: string;
  value: string;
  checked: boolean;
}

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

interface FilterOrder {
  start_date: string;
  end_date: string;
  search?: string;
  status?: string[];
  limit: number;
  offset: number;
  period: string;
}

@Component({
  selector: 'app-invoices',
  standalone: false,
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss'
})
export class InvoicesComponent {
  private destroy$ = new Subject<void>();
  loading = true;
  loadingMore = false;

  status: StatusItem[] = [
    { id: 'expired', label: 'Expired', value: 'expired', checked: false },
    { id: 'paid', label: 'Paid', value: 'paid', checked: false },
    { id: 'pending', label: 'Pending', value: 'pending', checked: false },
  ];

  search = '';
  private searchSubject = new Subject<string>();

  @ViewChild(DaterangepickerDirective, { static: false })
  pickerDirective!: DaterangepickerDirective;

  @ViewChild('dropdownFilter', { static: false }) dropdownFilter!: NgbDropdown;

  selected: DateRange = {
    startDate: moment(),
    endDate: moment(),
  };

  //let the type any
  ranges: any = {
    Today: [moment().startOf('day'), moment().endOf('day')],
    'Last 7 Days': [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')],
    'Last 1 Month': [moment().subtract(29, 'days').startOf('day'), moment().endOf('day')],
    'This Month': [moment().startOf('month'), moment().endOf('month')],
    'Custom Range': [moment(), moment()],
  };

  invalidDates: moment.Moment[] = [
    moment().add(2, 'days'),
    moment().add(3, 'days'),
    moment().add(5, 'days'),
  ];

  isInvalidDate = (m: moment.Moment): boolean => {
    return this.invalidDates.some((d) => d.isSame(m, 'day'));
  };

  setlocale: LocaleConfig = LocaleConfigDate;

  isDisPrev = true;
  isDisNext = false;
  totalList = 0;
  totalPage = 0;
  dataInvoices: detailInvoiceType[] = [];
  currentPage = 1;

  filterInvoices: FilterOrder = {
    start_date: moment().format('YYYY-MM-DDT00:00:00.660+00:00'),
    end_date: moment().format('YYYY-MM-DDT23:59:59.660+00:00'),
    search: '',
    status: [],
    limit: 10,
    offset: 0,
    period: 'custom'
  };

  @Output() filterResult = new EventEmitter<FilterOrder | DateRange | StatusItem[]>();

  constructor(
    private router: Router,
    private baseService: BaseService,
  ) { }

  ngOnInit(): void {
    setTimeout(() => {
      this.loading = false;
    }, 1000);

    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.filterInvoices.search = value;
        this.filterInvoices.offset = 0;
        this.dataInvoices = [];
        this.filterResult.emit(this.filterInvoices);
        this.getDataList();
      });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  onChangeSearch(value: string): void {
    if (value.length >= 2 || value.length === 0) {
      this.searchSubject.next(value);
    }
  }

  openDatepicker(): void {
    this.pickerDirective.open();
  }

  onChangeDate(e: {startDate: Dayjs, endDate: Dayjs} | null): void {
    if (!e) return;
    const endDate: Dayjs = e.endDate;
    const startDate: Dayjs = e.startDate;

    this.filterInvoices.start_date = startDate
      ? startDate.format('YYYY-MM-DDT00:00:00.660+00:00')
      : this.selected.startDate.format('YYYY-MM-DDT00:00:00.660+00:00');
    this.filterInvoices.end_date = endDate
      ? endDate.format('YYYY-MM-DDT23:59:59.660+00:00')
      : this.selected.endDate.format('YYYY-MM-DDT23:59:59.660+00:00');
    this.filterInvoices.offset = 0;

    localStorage.setItem('start-date-webhook', this.filterInvoices.start_date);
    localStorage.setItem('end-date-webhook', this.filterInvoices.end_date);

    this.dataInvoices = [];
    this.filterResult.emit(this.filterInvoices);
    this.getDataList();

    localStorage.setItem(
      'selectedSDate-webhook',
      JSON.stringify(
        startDate ? (startDate as Dayjs) : (this.selected.startDate as Dayjs)
      )
    );
    localStorage.setItem(
      'selectedEDate-webhook',
      JSON.stringify(
        endDate ? (endDate as Dayjs) : (this.selected.endDate as Dayjs)
      )
    );
  }

  getStatus(event: Event, item: StatusItem): void {
    const target = event.target as HTMLInputElement;
    item.checked = target.checked;

    const checkedStatuses = this.status
      .filter(s => s.checked)
      .map(s => s.value);

    this.filterInvoices.status = checkedStatuses.length > 0 ? checkedStatuses : undefined;
    this.filterInvoices.offset = 0;
    this.dataInvoices = [];
    this.filterResult.emit(this.filterInvoices);
    this.getDataList();
  }

  private updateLocalStorage(key: string, value: number): void {
    localStorage.setItem(key, value.toString());
  }

  onDetail(id: string): void {
    this.router.navigate(['/webhook', id]);
  }

  getDataList(): void {
    this.dropdownFilter?.close();

    if (this.loadingMore && this.filterInvoices.offset > 0) return;

    if (this.filterInvoices.offset > 0) {
      this.loadingMore = true;
    } else {
      this.loading = true;
    }

    this.baseService.getData(INVOICE_HISTORY, this.filterInvoices)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: { data: { invoices: detailInvoiceType[] } }) => {
          this.loading = false;
          this.loadingMore = false;

          if (this.filterInvoices.offset === 0) {
            this.dataInvoices = res.data.invoices;
          } else {
            this.dataInvoices = [...this.dataInvoices, ...res.data.invoices];
          }
        },
        error: () => {
          this.loading = false;
          this.loadingMore = false;
        }
      });
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 50) {
      if (!this.loading && !this.loadingMore && this.dataInvoices.length >= this.filterInvoices.limit) {
        this.filterInvoices.offset += this.filterInvoices.limit;
        this.getDataList();
      }
    }
  }

  getStatusClass(value: string | number) {
    switch (value) {
      case 'PAID':
        return 'status-success';
      case 'EXPIRED':
        return 'status-danger';
      case 'PENDING':
        return 'status-warning';
      default:
        return 'status-default';
    }
  }
  getStatusValue(value: string | number) {
    switch (value) {
      case 'PAID':
        return 'Paid';
      case 'EXPIRED':
        return 'Expired';
      case 'PENDING':
        return 'Pending';
      default:
        return 'Pending';
    }
  }

  getPaymentValue(value: string) {
    switch (value) {
      case 'bank_transfer':
        return 'Bank Transfer';
      case 'qris':
        return 'QRIS';
      case 'va':
        return 'VA';
      default:
        return value;
    }
  }
}
